<template>
  <v-card class="mb-6 pa-4">
    <h2>Create New Subscription Key</h2>
    <v-alert v-if="isBackupKey" type="warning" dense outlined class="mt-4">
      Creating a new backup/default key will invalidate the currently active one - only one backup
      key can be active at a time, same as for a company's keys.
    </v-alert>
    <div>
      <div v-for="(price, index) in prices" :key="index" class="d-flex align-center mb-4">
        <v-col cols="4">
          <v-text-field
            v-model="price.price"
            label="Price"
            type="number"
            min="0.01"
            step="1"
            required
          ></v-text-field>
        </v-col>
        <v-col cols="4">
          <v-text-field
            v-model="price.wholesalePrice"
            label="Wholesale Price"
            type="number"
            min="0"
            step="1"
          ></v-text-field>
        </v-col>
        <v-col :cols="prices.length > 1 ? 3 : 4">
          <v-select
            v-model="price.currency"
            :items="availableCurrencies(index)"
            label="Currency"
            required
          ></v-select>
        </v-col>

        <v-col v-if="prices.length > 1" cols="1">
          <v-btn icon color="error" @click="removePrice(index)">
            <v-icon>mdi-minus</v-icon>
          </v-btn>
        </v-col>
      </div>
      <v-col v-if="prices.length < allCurrencies.length" cols="12" class="d-flex justify-center">
        <v-btn icon color="primary" @click="addPrice">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-col>
      <v-row>
        <v-col cols="6">
          <v-text-field
            v-model="formData.subscriptionLength"
            label="Length (months)"
            type="number"
            min="1"
            required
          ></v-text-field>
        </v-col>
        <v-col cols="6">
          <v-menu
            v-model="datePicker"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="formattedDate"
                label="Active From"
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="formData.activeFrom"
              :min="minDate"
              @input="datePicker = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="6">
          <v-text-field
            v-model.number="formData.initialFocDays"
            label="Initial FOC length (days)"
            type="number"
          ></v-text-field>
        </v-col>
        <v-col cols="6">
          <v-text-field
            v-model.number="formData.prepaidPeriodDays"
            label="Prepaid period length (days)"
            type="number"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="6">
          <v-checkbox
            v-if="formData.prepaidPeriodDays > 0"
            v-model="formData.autoStartPrepaidPeriod"
            label="Auto start prepaid period"
          ></v-checkbox>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-btn color="primary" @click="handleCreate">Create</v-btn>
        </v-col>
      </v-row>
    </div>
  </v-card>
</template>

<script>
export default {
  name: 'SubscriptionKeyForm',
  props: {
    value: {
      type: Array,
      default: () => [{ price: 0, currency: 'CZK', wholesalePrice: null }],
    },
    latestActiveFrom: {
      type: Date,
      default: null,
    },
    isBackupKey: {
      type: Boolean,
      default: false,
    },
    canCreate: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      prices: this.value,
      allCurrencies: ['CZK', 'USD', 'EUR'],
      datePicker: false,
      formData: {
        subscriptionLength: 12,
        activeFrom: new Date().toISOString().split('T')[0],
        initialFocDays: 14,
        prepaidPeriodDays: 730,
        autoStartPrepaidPeriod: true,
      },
    }
  },
  computed: {
    formattedDate() {
      return this.formData.activeFrom ? new Date(this.formData.activeFrom).toLocaleDateString() : ''
    },
    minDate() {
      console.log('YEAH', this.latestActiveFrom)
      if (this.latestActiveFrom) {
        console.log('LATEST FROM', this.latestActiveFrom)
        return new Date(this.latestActiveFrom).toISOString().split('T')[0]
      }
      const today = new Date()
      return today.toISOString().split('T')[0]
    },
  },
  methods: {
    availableCurrencies(currentIndex) {
      return this.allCurrencies.filter((currency) => {
        return (
          this.prices.findIndex(
            (price, index) => price.currency === currency && index !== currentIndex,
          ) === -1
        )
      })
    },
    addPrice() {
      this.prices.push({
        price: 1,
        currency: this.availableCurrencies(this.prices.length)[0],
        wholesalePrice: null,
      })
      this.emitUpdate()
    },
    removePrice(index) {
      this.prices.splice(index, 1)
      this.emitUpdate()
    },
    validateForm() {
      const errors = []

      // Validate prices
      this.prices.forEach((price, index) => {
        const priceValue = parseFloat(price.price)
        if (!priceValue || priceValue <= 0) {
          errors.push(`Price ${index + 1} must be greater than 0`)
        }

        // Optional - only validate when the admin actually entered a wholesale price
        if (price.wholesalePrice !== null && price.wholesalePrice !== '') {
          const wholesaleValue = parseFloat(price.wholesalePrice)
          if (isNaN(wholesaleValue) || wholesaleValue < 0) {
            errors.push(`Wholesale price ${index + 1} must be 0 or greater`)
          }
        }
      })

      // Validate subscription length
      const length = parseFloat(this.formData.subscriptionLength)
      if (!length || length <= 0) {
        errors.push('Length must be greater than 0')
      }

      // Validate active from date
      if (!this.formData.activeFrom) {
        errors.push('Active From date is required')
      } else {
        const newDate = new Date(this.formData.activeFrom)
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        newDate.setHours(0, 0, 0, 0)

        if (newDate < today) {
          errors.push('Active From date cannot be in the past')
        }

        if (this.latestActiveFrom && newDate <= this.latestActiveFrom) {
          errors.push('Active From date must be after the latest existing subscription key date')
        }
      }

      return errors
    },
    emitUpdate() {
      // 'input' backs v-model="prices" in the parent, so it must emit the prices array itself
      // (matching the `value` prop type), not the full form payload - that goes out via 'create'.
      this.$emit('input', this.prices)
    },
    handleCreate() {
      if (!this.canCreate) {
        this.$toast.error('Please select a company or "Backup / Default klíč" first')
        return
      }

      const errors = this.validateForm()
      if (errors.length) {
        errors.forEach((error) => this.$toast.error(error))
        return
      }

      const data = {
        prices: this.prices,
        ...this.formData,
      }
      if (!(this.formData.prepaidPeriodDays > 0)) {
        delete data.autoStartPrepaidPeriod
      }

      this.$emit('create', data)
    },
  },
  watch: {
    prices: {
      deep: true,
      handler() {
        this.emitUpdate()
      },
    },
    'formData.subscriptionLength'() {
      this.emitUpdate()
    },
    'formData.activeFrom'() {
      this.emitUpdate()
    },
    'formData.initialFocDays'() {
      this.emitUpdate()
    },
    'formData.prepaidPeriodDays'() {
      this.emitUpdate()
    },
    'formData.autoStartPrepaidPeriod'() {
      this.emitUpdate()
    },
  },
}
</script>
